import React, { useState, useEffect } from 'react';
import { api } from '../../services/api';
import { useNotifications } from '../NotificationContext';

export const TokenManager: React.FC = () => {
  const { addNotification } = useNotifications();
  const [tokens, setTokens] = useState<any[]>([]);
  const [isGeneratingToken, setIsGeneratingToken] = useState(false);
  const [newTokenName, setNewTokenName] = useState('');
  const [createdModalToken, setCreatedModalToken] = useState<{ key: string; name: string } | null>(null);
  const [copiedTokenId, setCopiedTokenId] = useState<string | null>(null);

  useEffect(() => {
    fetchTokens();
  }, []);

  const fetchTokens = async () => {
    try {
      const data = await api.auth.getTokens();
      setTokens(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Failed to fetch tokens", error);
    }
  };

  const handleGenerateToken = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTokenName.trim()) return;
    try {
      const newToken = await api.auth.generateToken(newTokenName);
      setTokens(prev => [newToken, ...prev]);
      setIsGeneratingToken(false);
      setNewTokenName('');
      setCreatedModalToken({
        key: newToken.rawKey || newToken.secretKey || newToken.tokenPrefix,
        name: newToken.name || newTokenName,
      });
      addNotification({ title: 'Token Generated', message: `API Token "${newToken.name || newTokenName}" generated successfully.`, type: 'success' });
    } catch (error: any) {
      console.error("Failed to generate token", error);
      addNotification({ title: 'Generation Error', message: error.message || 'Failed to generate token', type: 'error' });
    }
  };

  const handleRevokeToken = async (id: string, name: string) => {
    if (!confirm(`Are you sure you want to revoke "${name}"? Apps using this key will lose access.`)) return;
    try {
      await api.auth.revokeToken(id);
      setTokens(prev => prev.filter(t => t.id !== id));
      addNotification({ title: 'Token Revoked', message: `Token "${name}" was revoked.`, type: 'success' });
    } catch (error: any) {
      console.error("Failed to revoke token", error);
      addNotification({ title: 'Revocation Error', message: error.message || 'Failed to revoke token', type: 'error' });
    }
  };

  return (
    <div className="bg-white dark:bg-slate-800 rounded-3xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
      <div className="p-8 border-b border-slate-100 dark:border-slate-700 flex justify-between items-center">
        <div>
          <h3 className="text-sm font-black text-slate-900 dark:text-white uppercase tracking-widest">API Authentication Tokens</h3>
          <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest mt-0.5">Bearer keys for server-to-server SubScale API calls</p>
        </div>
        <button 
          onClick={() => setIsGeneratingToken(true)}
          className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-xl font-bold text-[10px] uppercase tracking-widest transition-all shadow-md active:scale-95"
        >
          + Generate Token
        </button>
      </div>

      {isGeneratingToken && (
        <div className="p-8 bg-slate-50 dark:bg-slate-900/50 border-b border-slate-100 dark:border-slate-700 animate-in slide-in-from-top-4">
          <form onSubmit={handleGenerateToken} className="flex gap-4 items-end">
            <div className="flex-1 space-y-2">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Token Label / App Name</label>
              <input 
                type="text" 
                required
                placeholder="e.g. Connected SaaS Backend"
                className="w-full px-6 py-3 bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-700 rounded-xl font-bold text-sm outline-none focus:ring-4 focus:ring-indigo-50"
                value={newTokenName}
                onChange={(e) => setNewTokenName(e.target.value)}
              />
            </div>
            <div className="flex gap-2">
              <button 
                type="button" 
                onClick={() => setIsGeneratingToken(false)}
                className="px-6 py-3 text-[10px] font-black text-slate-400 uppercase tracking-widest hover:text-slate-600"
              >
                Cancel
              </button>
              <button 
                type="submit"
                className="bg-indigo-600 text-white px-6 py-3 rounded-xl font-black text-[10px] uppercase tracking-widest hover:bg-indigo-700 shadow-md transition-all"
              >
                Create
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="divide-y divide-slate-50 dark:divide-slate-700">
        {tokens.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">No active API tokens found</p>
          </div>
        ) : (
          tokens.map(token => (
            <div key={token.id} className="p-6 flex items-center justify-between hover:bg-slate-50/50 transition-colors">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-xl bg-indigo-50 dark:bg-slate-900 flex items-center justify-center text-indigo-600 dark:text-indigo-400">
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3m-3-3l-2.25-2.25"/></svg>
                </div>
                <div>
                  <p className="text-xs font-black text-slate-900 dark:text-white">{token.name}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <code className="text-[10px] font-mono bg-slate-100 dark:bg-slate-900 px-2 py-0.5 rounded text-indigo-600 dark:text-indigo-400 font-bold">{token.tokenPrefix || token.rawKey}</code>
                    <button 
                      onClick={() => {
                        const keyToCopy = token.rawKey || token.tokenPrefix;
                        navigator.clipboard.writeText(keyToCopy);
                        setCopiedTokenId(token.id);
                        addNotification({ title: 'Copied', message: 'API Token copied to clipboard', type: 'info' });
                        setTimeout(() => setCopiedTokenId(null), 2000);
                      }}
                      className="p-1 text-slate-400 hover:text-indigo-600 transition-colors flex items-center gap-1"
                      title="Copy API key"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect width="14" height="14" x="8" y="8" rx="2" ry="2"/><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/></svg>
                      {copiedTokenId === token.id && <span className="text-[9px] font-black text-emerald-500 uppercase">Copied!</span>}
                    </button>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-6">
                <div className="text-right">
                  <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Last Used</p>
                  <p className="text-[10px] font-bold text-slate-600 dark:text-slate-300">{token.lastUsed || 'Never'}</p>
                </div>
                <button 
                  onClick={() => handleRevokeToken(token.id, token.name)}
                  className="p-2 text-slate-300 hover:text-red-600 transition-colors"
                  title="Revoke Token"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/></svg>
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Generated Token Success Modal */}
      {createdModalToken && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-md" onClick={() => setCreatedModalToken(null)}></div>
          <div className="relative bg-white dark:bg-slate-800 rounded-[2.5rem] shadow-2xl w-full max-w-lg p-8 animate-in zoom-in-95 border border-slate-200 dark:border-slate-700">
            <div className="w-12 h-12 bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-400 rounded-2xl flex items-center justify-center mb-6">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
            </div>
            <h3 className="text-xl font-black text-slate-900 dark:text-white uppercase tracking-tight mb-2">API Key Created</h3>
            <p className="text-xs text-slate-500 font-medium mb-6">
              Your API key for <strong className="text-slate-900 dark:text-white">{createdModalToken.name}</strong> is generated. Store this key securely; it will not be displayed in full again.
            </p>

            <div className="space-y-2 mb-6">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Secret API Key</label>
              <div className="flex gap-2">
                <input 
                  readOnly 
                  type="text" 
                  value={createdModalToken.key} 
                  className="flex-1 px-4 py-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl font-mono text-xs text-indigo-600 dark:text-indigo-400 font-bold"
                />
                <button 
                  onClick={() => {
                    navigator.clipboard.writeText(createdModalToken.key);
                    addNotification({ title: 'Copied', message: 'API key copied to clipboard', type: 'success' });
                  }}
                  className="px-5 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-black uppercase tracking-widest transition-all shadow-md"
                >
                  Copy
                </button>
              </div>
            </div>

            <button 
              onClick={() => setCreatedModalToken(null)}
              className="w-full py-3.5 bg-slate-100 hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-900 dark:text-white rounded-xl text-xs font-black uppercase tracking-widest transition-all"
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
